import { DocumentNode } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

type UseQueryOptions = NonNullable<Parameters<typeof useQuery>[1]>

/**
 * This hook is a wrapper around the useQuery hook that adds a fetchPolicy of cache-and-network.
 * It also checks if the query is actually loading by checking if the query is in the cache.
 * If the query is not in the cache, it returns the loading state as true.
 *
 * Without this, useQuery also returns the loading state as true when refetching the query.
 * This resets the scroll position to top if you want to display a loading spinner on initial loading.
 *
 * @deprecated Use useQuery directly with fetchPolicy 'cache-and-network' if needed.
 *
 * You can know when it loads or loads more, please use the network status instead
 * const isLoadingMore = networkStatus === NetworkStatus.fetchMore
 */
export default function usePersistedCacheQuery(
    query: DocumentNode,
    options?: UseQueryOptions,
) {
    const result = useQuery(query, {
        ...(options as any),
        fetchPolicy: 'cache-and-network',
    })

    const isActuallyLoading =
        result.loading &&
        !result.client.readQuery({
            query,
            variables: (options as any)?.variables,
        })

    return { ...result, loading: isActuallyLoading }
}
